import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import {
  JobSchedule,
  JobScheduleCreate,
  JobSchedulePatch,
  MilestoneType,
  ScheduleStatus,
  milestoneDisplayNames,
} from "types/JobSchedule.types";

class JobScheduleViewStore {
  // Observables
  selectedMilestoneId: number | null = null;
  isListDialogOpen = false;
  isViewDialogOpen = false;
  isEditMode = false;
  formData: Partial<JobScheduleCreate> = {};
  isSubmitting = false;
  validationErrors: Record<string, string> = {};
  searchTerm = "";
  filterStatus: ScheduleStatus | "all" = "all";
  filterCriticalPath = false;
  currentJobId: number | null = null;
  databaseTableExists = true; // Assume it exists until proven otherwise

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setCurrentJobId = (jobId: number | null) => {
    this.currentJobId = jobId;
  };

  setSelectedMilestoneId = (id: number | null) => {
    this.selectedMilestoneId = id;
  };

  openListDialog = (jobId?: number) => {
    if (jobId) {
      this.currentJobId = jobId;
    }
    this.isListDialogOpen = true;
    this.loadMilestones();
  };

  openCreateDialog = (jobId?: number) => {
    this.isEditMode = false;
    const projectDashboardStore = this.rootStore.projectDashboardStore as any;
    const defaultJobId =
      jobId || this.currentJobId || projectDashboardStore?.project?.id;

    this.formData = {
      jobId: defaultJobId || 0,
      milestoneType: "custom" as MilestoneType,
      milestoneName: "",
      status: "upcoming",
      progressPercentage: 0,
      isVisible: true,
      sortOrder: 0,
      isCriticalPath: false,
    };
    this.validationErrors = {};

    // Open the JobScheduleForm dialog using routerStore
    this.rootStore.routerStore?.openDialog({
      dialogName: "JobScheduleForm",
    });
  };

  openViewDialog = (milestoneId: number) => {
    this.selectedMilestoneId = milestoneId;
    this.isViewDialogOpen = true;
    this.isEditMode = false;
  };

  openEditDialog = (milestoneId: number) => {
    const milestone = this.selectedMilestone;
    if (milestone) {
      this.selectedMilestoneId = milestoneId;
      this.isEditMode = true;
      this.formData = { ...milestone };
      this.validationErrors = {};

      // Open the JobScheduleForm dialog using routerStore
      this.rootStore.routerStore?.openDialog({
        dialogName: "JobScheduleForm",
      });
    }
  };

  closeDialogs = () => {
    this.isListDialogOpen = false;
    this.isViewDialogOpen = false;
    this.isEditMode = false;
    this.selectedMilestoneId = null;
    this.formData = {};
    this.validationErrors = {};
  };

  setFilterStatus = (status: ScheduleStatus | "all") => {
    this.filterStatus = status;
  };

  setFilterCriticalPath = (value: boolean) => {
    this.filterCriticalPath = value;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  updateFormData = (field: string, value: any) => {
    runInAction(() => {
      this.formData = {
        ...this.formData,
        [field]: value,
      };

      // Auto-fill milestone name based on type
      if (field === "milestoneType" && value !== "custom") {
        this.formData.milestoneName =
          milestoneDisplayNames[value as MilestoneType];
      }

      // Auto-update status based on progress
      if (field === "progressPercentage") {
        if (value === 100) {
          this.formData.status = "completed";
          if (!this.formData.actualDate) {
            this.formData.actualDate = new Date().toISOString();
          }
        } else if (value > 0) {
          this.formData.status = "inProgress";
        } else {
          this.formData.status = "upcoming";
        }
      }

      // Clear validation error for this field
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
    });
  };

  validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!this.formData.jobId) {
      errors.jobId = "Job is required";
    }
    if (!this.formData.milestoneType) {
      errors.milestoneType = "Milestone type is required";
    }
    if (!this.formData.milestoneName) {
      errors.milestoneName = "Milestone name is required";
    }
    if (
      this.formData.milestoneName &&
      this.formData.milestoneName.length > 255
    ) {
      errors.milestoneName = "Milestone name must be less than 255 characters";
    }
    if (
      this.formData.responsibleParty &&
      this.formData.responsibleParty.length > 255
    ) {
      errors.responsibleParty =
        "Responsible party must be less than 255 characters";
    }
    if (
      this.formData.progressPercentage !== undefined &&
      (this.formData.progressPercentage < 0 ||
        this.formData.progressPercentage > 100)
    ) {
      errors.progressPercentage = "Progress must be between 0 and 100";
    }

    this.validationErrors = errors;
    return Object.keys(errors).length === 0;
  };

  // Async actions
  submitMilestone = async () => {
    if (!this.validateForm()) {
      return false;
    }

    this.isSubmitting = true;
    try {
      const store = this.rootStore.jobScheduleStore as any;

      if (this.isEditMode && this.selectedMilestoneId) {
        // Update existing milestone
        const patchData: JobSchedulePatch = { ...this.formData };
        delete (patchData as any).jobId; // Remove jobId from patch
        await store.patch(this.selectedMilestoneId, patchData);
      } else {
        // Create new milestone
        const createData: JobScheduleCreate = this
          .formData as JobScheduleCreate;

        // Set original scheduled date if not set
        if (createData.scheduledDate && !createData.originalScheduledDate) {
          createData.originalScheduledDate = createData.scheduledDate;
        }

        await store.create(createData);
      }

      runInAction(() => {
        this.closeDialogs();
        this.isSubmitting = false;
      });
      // Reload milestones after submission
      await this.loadMilestones();
      return true;
    } catch (error: any) {
      // Handle database table not existing error gracefully
      if (error?.message?.includes("Invalid object name 'jobSchedules'")) {
        console.warn(
          "Job schedules table not yet created in database. Please run database migrations."
        );
        alert(
          "The project schedule feature is not yet available. Please contact your administrator to set up the database."
        );
      } else {
        console.error("Error submitting milestone:", error);
      }
      runInAction(() => {
        this.isSubmitting = false;
      });
      return false;
    }
  };

  updateMilestoneStatus = async (
    milestoneId: number,
    newStatus: ScheduleStatus
  ) => {
    try {
      const store = this.rootStore.jobScheduleStore as any;
      const updateData: JobSchedulePatch = {
        status: newStatus,
      };

      // Add completion data if completing
      if (newStatus === "completed") {
        updateData.progressPercentage = 100;
        updateData.actualDate = new Date().toISOString();
      }

      await store.patch(milestoneId, updateData);
      return true;
    } catch (error) {
      console.error("Error updating milestone status:", error);
      return false;
    }
  };

  updateMilestoneProgress = async (milestoneId: number, progress: number) => {
    try {
      const store = this.rootStore.jobScheduleStore as any;
      const updateData: JobSchedulePatch = {
        progressPercentage: progress,
      };

      // Auto-update status based on progress
      if (progress === 100) {
        updateData.status = "completed";
        updateData.actualDate = new Date().toISOString();
      } else if (progress > 0) {
        updateData.status = "inProgress";
      }

      await store.patch(milestoneId, updateData);
      return true;
    } catch (error) {
      console.error("Error updating milestone progress:", error);
      return false;
    }
  };

  loadMilestones = async () => {
    try {
      const store = this.rootStore.jobScheduleStore as any;

      // Only try to load if the store exists
      if (!store) {
        console.warn("JobScheduleStore not available");
        return;
      }

      const query: any = {
        $limit: 500,
      };

      // Add current job filter if available
      if (this.currentJobId) {
        query.jobId = this.currentJobId;
      }

      await store.find({ query });
      // If successful, table exists
      runInAction(() => {
        this.databaseTableExists = true;
      });
    } catch (error: any) {
      // Handle database table not existing error gracefully
      if (error?.message?.includes("Invalid object name 'jobSchedules'")) {
        console.warn(
          "Job schedules table not yet created in database. Please run database migrations."
        );
        runInAction(() => {
          this.databaseTableExists = false;
        });
        // Don't re-throw the error to prevent dialog crash
      } else {
        console.error("Error loading milestones:", error);
      }
    }
  };

  createDefaultMilestones = async (jobId: number) => {
    // Check if store exists before trying to create
    const store = this.rootStore.jobScheduleStore as any;
    if (!store) {
      console.warn("JobScheduleStore not available");
      return false;
    }

    const defaultMilestones: Partial<JobScheduleCreate>[] = [
      {
        jobId,
        milestoneType: "projectKickoff",
        milestoneName: "Project Kickoff",
        status: "completed",
        progressPercentage: 100,
        isCriticalPath: true,
        sortOrder: 10,
      },
      {
        jobId,
        milestoneType: "designApproval",
        milestoneName: "Design Approval",
        status: "upcoming",
        progressPercentage: 0,
        isCriticalPath: true,
        sortOrder: 20,
      },
      {
        jobId,
        milestoneType: "shopDrawingSubmission",
        milestoneName: "Shop Drawing Submission",
        status: "upcoming",
        progressPercentage: 0,
        isCriticalPath: true,
        sortOrder: 30,
      },
      {
        jobId,
        milestoneType: "productionStart",
        milestoneName: "Production Start",
        status: "upcoming",
        progressPercentage: 0,
        isCriticalPath: true,
        sortOrder: 40,
      },
      {
        jobId,
        milestoneType: "firstPanelCast",
        milestoneName: "First Panel Cast",
        status: "upcoming",
        progressPercentage: 0,
        sortOrder: 50,
      },
      {
        jobId,
        milestoneType: "shippingStart",
        milestoneName: "Shipping Start",
        status: "upcoming",
        progressPercentage: 0,
        isCriticalPath: true,
        sortOrder: 60,
      },
      {
        jobId,
        milestoneType: "erectionStart",
        milestoneName: "Erection Start",
        status: "upcoming",
        progressPercentage: 0,
        isCriticalPath: true,
        sortOrder: 70,
      },
      {
        jobId,
        milestoneType: "projectCompletion",
        milestoneName: "Project Completion",
        status: "upcoming",
        progressPercentage: 0,
        isCriticalPath: true,
        sortOrder: 80,
      },
    ];

    try {
      for (const milestone of defaultMilestones) {
        await store.create(milestone);
      }
      // Reload milestones after creating defaults
      await this.loadMilestones();
      return true;
    } catch (error: any) {
      // Handle database table not existing error gracefully
      if (error?.message?.includes("Invalid object name 'jobSchedules'")) {
        console.warn(
          "Job schedules table not yet created in database. Please run database migrations."
        );
        alert(
          "The project schedule feature is not yet available. Please contact your administrator to set up the database."
        );
      } else {
        console.error("Error creating default milestones:", error);
      }
      return false;
    }
  };

  // Computed values
  get selectedMilestone(): JobSchedule | undefined {
    if (!this.selectedMilestoneId) return undefined;
    return (this.rootStore.jobScheduleStore as any)?.getById(
      this.selectedMilestoneId
    );
  }

  get milestones(): JobSchedule[] {
    const store = this.rootStore.jobScheduleStore as any;
    if (!store?.entities) return [];

    let milestones = [...store.entities];

    // Filter by current job
    if (this.currentJobId) {
      milestones = milestones.filter(
        (m: JobSchedule) => m.jobId === this.currentJobId
      );
    }

    // Apply status filter
    if (this.filterStatus !== "all") {
      milestones = milestones.filter(
        (m: JobSchedule) => m.status === this.filterStatus
      );
    }

    // Apply critical path filter
    if (this.filterCriticalPath) {
      milestones = milestones.filter((m: JobSchedule) => m.isCriticalPath);
    }

    // Apply search
    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      milestones = milestones.filter(
        (m: JobSchedule) =>
          m.milestoneName.toLowerCase().includes(searchLower) ||
          m.description?.toLowerCase().includes(searchLower) ||
          m.responsibleParty?.toLowerCase().includes(searchLower)
      );
    }

    // Sort by sortOrder and then scheduledDate
    return milestones.sort((a: JobSchedule, b: JobSchedule) => {
      if (a.sortOrder !== b.sortOrder) {
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      }
      if (a.scheduledDate && b.scheduledDate) {
        return (
          new Date(a.scheduledDate).getTime() -
          new Date(b.scheduledDate).getTime()
        );
      }
      return 0;
    });
  }

  get milestoneCounts() {
    const milestones = this.milestones;
    return {
      total: milestones.length,
      upcoming: milestones.filter((m) => m.status === "upcoming").length,
      pending: milestones.filter((m) => m.status === "pending").length,
      inProgress: milestones.filter((m) => m.status === "inProgress").length,
      completed: milestones.filter((m) => m.status === "completed").length,
      delayed: milestones.filter((m) => m.status === "delayed").length,
      onHold: milestones.filter((m) => m.status === "onHold").length,
      critical: milestones.filter((m) => m.isCriticalPath).length,
    };
  }

  get completionPercentage() {
    const milestones = this.milestones;
    if (milestones.length === 0) return 0;

    const totalProgress = milestones.reduce(
      (sum, m) => sum + (m.progressPercentage || 0),
      0
    );
    return Math.round(totalProgress / milestones.length);
  }

  get upcomingMilestones() {
    return this.milestones
      .filter((m) => m.status !== "completed" && m.status !== "cancelled")
      .slice(0, 5);
  }

  get delayedMilestones() {
    return this.milestones.filter((m) => m.status === "delayed");
  }

  get jobs() {
    return (this.rootStore.jobStore as any)?.entities || [];
  }

  get users() {
    return (this.rootStore.usersStore as any)?.entities || [];
  }

  get currentJob() {
    if (!this.currentJobId) return null;
    return this.jobs.find((j: any) => j.id === this.currentJobId);
  }
}

export default JobScheduleViewStore;
